/**
 * What to tell the user when starting a subscription doesn't succeed.
 *
 * Every purchase path used to show one sentence — "Could not start the
 * subscription. Please try again." — for causes that need completely different
 * responses, including one (no billing details yet) that isn't a failure at all
 * and one (our own payment account isn't configured) where retrying can never
 * work. The backend now names the cause in `code`; this turns it into words.
 */

// The backend answers 409 with this when the buyer has no payment profile. The
// caller collects the details and retries rather than showing an error.
export const PAYMENT_PROFILE_REQUIRED = "payment_profile_required";

const MESSAGES = {
  [PAYMENT_PROFILE_REQUIRED]: "We need your billing details before the plan can start.",
  platform_not_configured:
    "We can't take payments right now — please contact support and we'll sort it out.",
  provider_error: "Our payment provider couldn't start the plan. Please try again shortly.",
};

/** True when the response is the "collect billing details and retry" case. */
export function needsPaymentProfile(res) {
  return !!res && res.status === 409 && res.code === PAYMENT_PROFILE_REQUIRED;
}

/**
 * The message for a failed subscribe response. Prefers the named code, falls
 * back to whatever the API said, and only then to something generic.
 *
 * Note these controllers return the parsed BODY with `status` overwritten by the
 * HTTP code — so `res.detail`/`res.code` are read off the response itself, and
 * there is no `res.json`.
 */
export function subscribeErrorMessage(res) {
  if (!res) return "Could not start the subscription. Please try again.";
  return (
    MESSAGES[res.code] ||
    res.detail ||
    res.error ||
    "Could not start the subscription. Please try again."
  );
}


/**
 * What the purchase screen says while, and after, the first charge is taken.
 *
 * The attach endpoint answers 201 when the card is STORED, not when it is
 * charged. Saying "active" on that 201 — which this app did — told a customer
 * whose card was declined that their subscription was live. The screen now
 * polls /usage/offices/<id>/subscription-status/ and speaks from its `state`.
 *
 * Returns {kind: "success"|"error"|"info"|"wait", text} — `wait` means keep
 * polling and say nothing yet.
 */
export function chargeOutcomeMessage(state) {
  switch (state) {
    case "active":
      return { kind: "success", text: "Your subscription is active — thanks!" };
    case "declined":
      return { kind: "error", text: "Your card was declined. Please try another card." };
    case "provider_error":
      return {
        kind: "error",
        text: "Our payment provider couldn't take the payment. Nothing was charged — please try again shortly.",
      };
    case "needs_review":
      return {
        kind: "info",
        text: "We're confirming your payment with our provider. You'll hear from us shortly — please don't enter the card again.",
      };
    case "confirming":
    case "awaiting_card":
      return { kind: "wait", text: "Confirming your payment…" };
    default:
      return {
        kind: "info",
        text: "We're confirming your payment. Please check back in a moment.",
      };
  }
}

/** The states that end the poll. Everything else keeps waiting. */
export const FINAL_CHARGE_STATES = ["active", "declined", "provider_error", "needs_review"];
